import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, Alert, IconButton,
  FormControlLabel, Switch, CircularProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LockIcon from '@mui/icons-material/Lock';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { jwtDecode } from 'jwt-decode';
import AccountFormModal from '../modals/AccountFormModal';
import TwoFactorSetupModal from '../modals/TwoFactorSetupModal';
import { apiFetch } from '../apiFetch';
import {
  isPlatformAuthenticatorAvailable,
  parseRegistrationOptions,
  prepareCredentialForServer,
  getDeviceName,
} from '../utils/webauthn';
import {
  isPWAInstalled,
  detectPWAPlatform,
  canInstallPWA,
} from '../utils/pwa';
import PWAPromptModal from '../modals/PWAPromptModal';
import SchoolIcon from '@mui/icons-material/School';
import { TutorialContext } from '../components/TutorialProvider';

interface TokenPayload {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  is_staff: boolean;
  is_superuser: boolean;
}

interface WebAuthnCredential {
  id: number;
  device_name: string;
  created_at: string;
  last_used_at: string | null;
}

const Account: React.FC = () => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const tutorialContext = React.useContext(TutorialContext);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // User data from token
  const [userData, setUserData] = useState<TokenPayload | null>(null);

  // Biometric state
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricCredentials, setBiometricCredentials] = useState<WebAuthnCredential[]>([]);
  const [biometricLoading, setBiometricLoading] = useState(true);
  const [biometricRegistering, setBiometricRegistering] = useState(false);

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(true);
  const [twoFactorSetupOpen, setTwoFactorSetupOpen] = useState(false);
  const [twoFactorCreatedAt, setTwoFactorCreatedAt] = useState<string | null>(null);

  // 2FA disable dialog state
  const [disableTwoFactorOpen, setDisableTwoFactorOpen] = useState(false);
  const [disableTwoFactorCode, setDisableTwoFactorCode] = useState('');
  const [disableTwoFactorLoading, setDisableTwoFactorLoading] = useState(false);
  const [disableTwoFactorError, setDisableTwoFactorError] = useState<string | null>(null);

  // PWA state
  const [pwaInstalled, setPwaInstalled] = useState(false);
  const [pwaPlatform, setPwaPlatform] = useState<string | null>(null);
  const [pwaPromptOpen, setPwaPromptOpen] = useState(false);
  const [pwaLoading, setPwaLoading] = useState(true);

  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const [hardDelete, setHardDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Check WebAuthn support on mount
  useEffect(() => {
    const checkSupport = async () => {
      const supported = await isPlatformAuthenticatorAvailable();
      setBiometricSupported(supported);
    };
    checkSupport();
  }, []);

  // Load user data and biometric status
  useEffect(() => {
    loadUserData();
    loadBiometricStatus();
    loadTwoFactorStatus();
    loadPwaStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserData = () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const payload = jwtDecode<TokenPayload>(token);
        setUserData(payload);
      } catch (err) {
        console.error('Failed to decode token:', err);
      }
    }
  };

  const loadBiometricStatus = async () => {
    setBiometricLoading(true);
    try {
      const data = await apiFetch(`${apiUrl}/api/webauthn/credentials/`);
      setBiometricEnabled(data.has_credentials);
      setBiometricCredentials(data.credentials);
    } catch (err) {
      console.error('Failed to load biometric status:', err);
    } finally {
      setBiometricLoading(false);
    }
  };

  const loadTwoFactorStatus = async () => {
    setTwoFactorLoading(true);
    try {
      const data = await apiFetch(`${apiUrl}/api/2fa/status/?purpose=login`);
      setTwoFactorEnabled(data.enabled);
      setTwoFactorCreatedAt(data.created_at || null);
    } catch (err) {
      console.error('Failed to load 2FA status:', err);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const loadPwaStatus = async () => {
    setPwaLoading(true);
    try {
      const data = await apiFetch(`${apiUrl}/api/account/profile/`);
      setPwaInstalled(data.pwa_installed || isPWAInstalled());
      setPwaPlatform(data.pwa_platform || null);

      // Auto-update if we detect PWA but backend doesn't know
      if (isPWAInstalled() && !data.pwa_installed) {
        const platform = detectPWAPlatform();
        await apiFetch(`${apiUrl}/api/account/profile/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pwa_installed: true, pwa_platform: platform }),
        });
        setPwaInstalled(true);
        setPwaPlatform(platform);
      }
    } catch (err) {
      console.error('Failed to load PWA status:', err);
    } finally {
      setPwaLoading(false);
    }
  };

  const handlePwaInstalled = () => {
    setPwaInstalled(true);
    setPwaPlatform(detectPWAPlatform());
    setSuccessMessage('App installed successfully!');
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleTwoFactorSetupSuccess = () => {
    loadTwoFactorStatus();
    setSuccessMessage('Two-factor authentication enabled successfully!');
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleOpenDisableTwoFactor = () => {
    setDisableTwoFactorCode('');
    setDisableTwoFactorError(null);
    setDisableTwoFactorOpen(true);
  };

  const handleCloseDisableTwoFactor = () => {
    setDisableTwoFactorOpen(false);
    setDisableTwoFactorCode('');
    setDisableTwoFactorError(null);
  };

  const handleTwoFactorDisable = async () => {
    if (disableTwoFactorCode.length !== 6) {
      setDisableTwoFactorError('Please enter a 6-digit code');
      return;
    }

    setDisableTwoFactorLoading(true);
    setDisableTwoFactorError(null);

    try {
      await apiFetch(`${apiUrl}/api/2fa/disable/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purpose: 'login', code: disableTwoFactorCode }),
      });
      setTwoFactorEnabled(false);
      setTwoFactorCreatedAt(null);
      handleCloseDisableTwoFactor();
      setSuccessMessage('Two-factor authentication disabled.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: unknown) {
      const error = err as Error;
      setDisableTwoFactorError(error.message || 'Failed to disable 2FA.');
    } finally {
      setDisableTwoFactorLoading(false);
    }
  };

  const handleBiometricToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const enable = event.target.checked;

    if (enable) {
      await registerBiometric();
    } else {
      await deleteAllCredentials();
    }
  };

  const registerBiometric = async () => {
    setBiometricRegistering(true);
    setErrorMessage(null);

    try {
      // Step 1: Get registration options from server
      const optionsResponse = await apiFetch(`${apiUrl}/api/webauthn/register/options/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      // Step 2: Create credential with browser API
      const publicKeyOptions = parseRegistrationOptions(optionsResponse);
      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to create credential');
      }

      // Step 3: Send credential to server for verification
      const credentialForServer = prepareCredentialForServer(credential);
      await apiFetch(`${apiUrl}/api/webauthn/register/verify/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credentialForServer,
          device_name: getDeviceName(),
        }),
      });

      setSuccessMessage('Biometric login enabled successfully!');
      setTimeout(() => setSuccessMessage(null), 4000);
      loadBiometricStatus();

    } catch (err: unknown) {
      const error = err as Error & { name?: string };
      if (error.name === 'NotAllowedError') {
        setErrorMessage('Biometric authentication was cancelled or not available.');
      } else {
        setErrorMessage(error.message || 'Failed to enable biometric login.');
      }
      setTimeout(() => setErrorMessage(null), 6000);
    } finally {
      setBiometricRegistering(false);
    }
  };

  const deleteAllCredentials = async () => {
    try {
      for (const cred of biometricCredentials) {
        await apiFetch(`${apiUrl}/api/webauthn/credentials/${cred.id}/`, {
          method: 'DELETE',
        });
      }
      setSuccessMessage('Biometric login disabled.');
      setTimeout(() => setSuccessMessage(null), 4000);
      loadBiometricStatus();
    } catch (err: unknown) {
      const error = err as Error;
      setErrorMessage(error.message || 'Failed to disable biometric login.');
      setTimeout(() => setErrorMessage(null), 6000);
    }
  };

  const handleEditClick = () => {
    setModalOpen(true);
  };

  const handleOpenDeleteDialog = () => {
    setDeleteStep(1);
    setHardDelete(false);
    setDeletePassword('');
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletePassword('');
    setDeleteError(null);
  };

  const handleDeleteChoose = (hard: boolean) => {
    setHardDelete(hard);
    setDeleteStep(2);
    setDeleteError(null);
    setDeletePassword('');
  };

  const handleDeleteConfirm = async () => {
    if (!deletePassword) return;

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      await apiFetch(`${apiUrl}/api/account/delete/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: deletePassword, hard_delete: hardDelete }),
      });

      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.dispatchEvent(new Event('authChange'));
      navigate(`/login?message=${hardDelete ? 'account_deleted' : 'account_deactivated'}`);
    } catch (err: unknown) {
      const error = err as Error;
      setDeleteError(error.message || 'Failed to delete account.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSuccess = () => {
    // Reload user data from the refreshed token
    loadUserData();
    setSuccessMessage('Profile updated successfully.');
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/')} aria-label="back to home">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Account Details</Typography>
        </Box>
        <Box className="page-header-buttons">
          {userData && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
            >
              Edit Profile
            </Button>
          )}
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {userData ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          <Typography variant="body1">
            <strong>First Name:</strong> {userData.first_name}
          </Typography>
          <Typography variant="body1">
            <strong>Last Name:</strong> {userData.last_name}
          </Typography>
          <Typography variant="body1">
            <strong>Email:</strong> {userData.email}
          </Typography>
          <Typography variant="body1">
            <strong>Username:</strong> {userData.username}
          </Typography>
          {(userData.is_staff === true || userData.is_superuser === true) && (
            <Typography variant="body1">
              <strong>Role:</strong>{' '}
              {[
                userData.is_superuser === true && 'Superuser',
                userData.is_staff === true && 'Staff',
              ].filter(Boolean).join(', ')}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body1">No account details available. Please log in.</Typography>
      )}

      {/* Biometric Login Section */}
      {biometricSupported && userData && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FingerprintIcon />
              Biometric Login
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use your fingerprint, Face ID, or Windows Hello to log in quickly and securely.
            </Typography>

            {biometricLoading ? (
              <CircularProgress size={24} />
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={biometricEnabled}
                    onChange={handleBiometricToggle}
                    disabled={biometricRegistering}
                  />
                }
                label={biometricRegistering ? 'Setting up...' : (biometricEnabled ? 'Enabled' : 'Disabled')}
              />
            )}

            {biometricCredentials.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Registered devices:
                </Typography>
                {biometricCredentials.map((cred) => (
                  <Typography key={cred.id} variant="body2" sx={{ ml: 2 }}>
                    • {cred.device_name} - registered {new Date(cred.created_at).toLocaleDateString()}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Two-Factor Authentication Section */}
      {userData && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon />
              Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add an extra layer of security by requiring a code from your authenticator app when logging in.
            </Typography>

            {twoFactorLoading ? (
              <CircularProgress size={24} />
            ) : twoFactorEnabled ? (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Two-factor authentication is enabled.
                  {twoFactorCreatedAt && (
                    <Typography variant="caption" display="block">
                      Enabled on {new Date(twoFactorCreatedAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Alert>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleOpenDisableTwoFactor}
                >
                  Disable 2FA
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={() => setTwoFactorSetupOpen(true)}
                startIcon={<LockIcon />}
              >
                Enable Two-Factor Authentication
              </Button>
            )}
          </Box>
        </>
      )}

      {/* PWA Installation Section */}
      {userData && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIphoneIcon />
              App Installation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Install Grocery Price Checker on your device for quick access and offline support.
            </Typography>

            {pwaLoading ? (
              <CircularProgress size={24} />
            ) : pwaInstalled || isPWAInstalled() ? (
              <Alert severity="success">
                App is installed on your device.
                {pwaPlatform && (
                  <Typography variant="caption" display="block">
                    Platform: {pwaPlatform.charAt(0).toUpperCase() + pwaPlatform.slice(1)}
                  </Typography>
                )}
              </Alert>
            ) : canInstallPWA() ? (
              <Button
                variant="contained"
                onClick={() => setPwaPromptOpen(true)}
                startIcon={<PhoneIphoneIcon />}
              >
                Install App
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                App installation is not available in this browser. Try using Chrome, Safari, or Edge.
              </Typography>
            )}
          </Box>
        </>
      )}

      {/* Edit Account Modal */}
      <AccountFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleFormSuccess}
        currentFirstName={userData?.first_name || ''}
        currentLastName={userData?.last_name || ''}
      />

      {/* 2FA Setup Modal */}
      <TwoFactorSetupModal
        open={twoFactorSetupOpen}
        onClose={() => setTwoFactorSetupOpen(false)}
        onSuccess={handleTwoFactorSetupSuccess}
        purpose="login"
      />

      {/* Disable 2FA Confirmation Dialog */}
      <Dialog open={disableTwoFactorOpen} onClose={handleCloseDisableTwoFactor} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="error" />
          Disable Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to disable two-factor authentication? This will remove the extra
            layer of security from your account. You'll only need your password to log in.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If you want to re-enable 2FA later, you'll need to set it up again with your authenticator app.
          </Typography>
          {disableTwoFactorError && (
            <Alert severity="error" sx={{ mb: 2 }}>{disableTwoFactorError}</Alert>
          )}
          <TextField
            label="Current 2FA Code"
            variant="outlined"
            fullWidth
            value={disableTwoFactorCode}
            onChange={(e) => setDisableTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5rem' },
            }}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && disableTwoFactorCode.length === 6) {
                handleTwoFactorDisable();
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Enter the 6-digit code from your authenticator app to confirm.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDisableTwoFactor} disabled={disableTwoFactorLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleTwoFactorDisable}
            disabled={disableTwoFactorLoading || disableTwoFactorCode.length !== 6}
          >
            {disableTwoFactorLoading ? <CircularProgress size={24} /> : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tutorials Section */}
      {userData && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SchoolIcon />
              Tutorials
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reset all tutorials to see the onboarding guides again on each page.
            </Typography>
            <Button
              variant="outlined"
              onClick={async () => {
                if (tutorialContext) {
                  await tutorialContext.resetAll();
                  setSuccessMessage('All tutorials have been reset. You will see the guides again on each page.');
                  setTimeout(() => setSuccessMessage(null), 4000);
                }
              }}
              startIcon={<SchoolIcon />}
            >
              Reset All Tutorials
            </Button>
          </Box>
        </>
      )}

      {/* Delete Account Section */}
      {userData && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteIcon />
              Delete Account
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Permanently deactivate or delete your account.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleOpenDeleteDialog}
            >
              Delete Account
            </Button>
          </Box>
        </>
      )}

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        {deleteStep === 1 ? (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon color="warning" />
              Delete Account
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="error" sx={{ mb: 3, fontWeight: 'bold' }}>
                This action cannot be undone.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Button
                    variant="outlined"
                    color="warning"
                    fullWidth
                    onClick={() => handleDeleteChoose(false)}
                    sx={{ textTransform: 'none', textAlign: 'left', p: 2 }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Deactivate Account</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Your account will be disabled and you will be logged out. Your data (groceries, prices, shopping lists, stores) will be kept. Contact us if you want to reactivate.
                      </Typography>
                    </Box>
                  </Button>
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    onClick={() => handleDeleteChoose(true)}
                    sx={{ textTransform: 'none', textAlign: 'left', p: 2 }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'white' }}>Permanently Delete Everything</Typography>
                      <Typography variant="caption" sx={{ color: 'inherit' }}>
                        Your account AND all your data will be permanently erased. This includes all groceries, prices, shopping lists, and stores. This cannot be reversed.
                      </Typography>
                    </Box>
                  </Button>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon color="error" />
              Confirm {hardDelete ? 'Permanent Deletion' : 'Account Deactivation'}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {hardDelete
                  ? 'You are about to permanently delete your account and all associated data.'
                  : 'You are about to deactivate your account. You will be logged out.'}
              </Typography>
              {hardDelete && (
                <Typography variant="body2" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
                  All your data will be permanently deleted.
                </Typography>
              )}
              {deleteError && (
                <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>
              )}
              <TextField
                label="Enter your password to confirm"
                type="password"
                variant="outlined"
                fullWidth
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && deletePassword) {
                    handleDeleteConfirm();
                  }
                }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading || !deletePassword}
              >
                {deleteLoading ? <CircularProgress size={24} /> : (hardDelete ? 'Permanently Delete' : 'Deactivate Account')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* PWA Install Prompt Modal */}
      <PWAPromptModal
        open={pwaPromptOpen}
        onClose={() => setPwaPromptOpen(false)}
        onInstalled={handlePwaInstalled}
      />
    </Container>
  );
};

export default Account;
